import { Component } from '@angular/core';
import { SearchParams } from '../../search/models/search-params.model';
import { SearchStatus } from '../../search/models/search-status.model';
import { SearchService } from '../../search/search.service';
import { AlertService, ResourceService } from 'sce-core';
import { SharedServiceRegistry } from '../../shared-service-registry';
import { CoreServiceRegistry } from '../../../core/core-service-registry';
import { ButtonItem } from 'app/shared/button/button.model';
import { DISPLAY_MODE } from 'app/shared/search/search-constants';
import { DataTableConfig } from 'app/shared/search/models/data-table-config';
import { SearchableEntityDataEditorService } from 'app/shared/services/searchable-entity/searchable-entity-data-editor.service';
import { Clause, Condition, SearchCriteria } from 'app/shared/search/models/search-filter-model';
import { META_DATA, SEARCH_KEYS, TABLE_STYLE, YES } from 'app/constants';
import { SerialNumberPopupBackendService } from './services/serial-number-popup-backend.service';
import { SerialNumberPopupActionService } from './services/serial-number-popup-action.service';

@Component({
  selector: 'lfwms-serial-number-popup',
  templateUrl: './serial-number-popup.component.html',
  styleUrls: ['./serial-number-popup.component.css']
})
export class SerialNumberPopupComponent {
  public searchAccordionTitle;
  public searchParams: SearchParams;
  public lookupStatus: SearchStatus;
  public lookupSearchService: SearchService;
  public hideOnSearch: boolean = false;
  public alertService: AlertService;
  public isLookup: boolean = true;
  public tableId: string = 'receiptserialnotd';
  public storer: string = '';
  public receiptKey: string = '';

  public buttonArray: Array<ButtonItem> = [
    new ButtonItem('Edit', this.resourceService.get('btn_edit'), '', 'pencil', []),
    new ButtonItem('Save', this.resourceService.get('btn_save'), '', 'floppy-disk', []),
    new ButtonItem('Delete_Main', this.resourceService.get('btn_delete'), '', 'trash', [
      new ButtonItem('Delete Line Item(s)',
          this.resourceService.get('btn_delete_line'), '', 'trash', []),
      new ButtonItem('Delete All Line Items',
            this.resourceService.get('btn_delete_all_lines'), '', 'trash', []),
    ]),
  ];

  constructor(
    private readonly resourceService: ResourceService,
    private readonly sharedServiceRegistry: SharedServiceRegistry,
    private readonly coreServiceRegistry: CoreServiceRegistry,
    private readonly backendService: SerialNumberPopupBackendService,
    private readonly actionService: SerialNumberPopupActionService
  ) {
    this.resourceService = this.coreServiceRegistry.resourceService;
    this.alertService = this.sharedServiceRegistry.alertService;
  }

  initializeComponents(storerKey: string, receiptKey: string) {
    this.lookupSearchService = this.createSearchService();
    this.storer = storerKey;
    this.receiptKey = receiptKey;
    this.actionService.initializeService(
      this.lookupSearchService,
      this.tableId,
      this.receiptKey
    );
    const searchParams = this.lookupSearchService.searchParams;
    this.searchParams = searchParams;
    this.lookupSearchService.setSearchCriteria(this.createSearchCriteria());
    this.lookupSearchService.fetchMetadata().subscribe(() => {
      this.lookupSearchService.performSearch();
    });
    this.initializeButtons(searchParams);
  }
  
  createSearchService():  SearchService {
    this.lookupStatus = new SearchStatus();
    const searchParams = new SearchParams();
    searchParams.tableId = this.tableId;
    searchParams.isFullFetch = true;
    searchParams.alertService = this.sharedServiceRegistry.alertService;
    searchParams.tableEditor = new SearchableEntityDataEditorService(
      this.coreServiceRegistry,
      this.sharedServiceRegistry
    );
    searchParams.dataTableConfig = new DataTableConfig();
    searchParams.dataTableConfig.editable = true;
    searchParams.dataTableConfig.showIdAsAHyperLink = true;
    searchParams.dataTableConfig.isColumnEditable = this.isColumnEditable;
    searchParams.dataTableConfig.rowStyle = this.getRowStyle;
    
    const searchService = this.sharedServiceRegistry.createSearchService(searchParams, this.lookupStatus);
    return searchService;
  }
  
  enableButtons() {
    const inEditMode = this.lookupSearchService.displayMode === DISPLAY_MODE.EDIT;
    this.buttonArray[0].isEnabled = !inEditMode;
    this.buttonArray[0].isVisible = !inEditMode;
    this.buttonArray[1].isEnabled = inEditMode;
    this.buttonArray[1].isVisible = inEditMode;
    this.buttonArray[2].isEnabled = inEditMode;
    this.buttonArray[2].isVisible = inEditMode;
  }

  initializeButtons(searchParams: SearchParams) {
    searchParams.actionButtons =
      this.sharedServiceRegistry.buttonFactoryService.loadButtonsByPrivilege(this.buttonArray);
    for (let button of this.buttonArray) {
      button.isEnabled = false;
      button.isVisible = false;
    };
    // Initialize buttons only if storer config allows serial number log maintenance
    this.backendService.getStorerConfig(this.storer).subscribe(
      (response: any) => {
        if(response && response.sValue === "1") {
          this.enableButtons();
        }
      },
      (error: any) => {
        this.alertService.clearAll().error(this.resourceService.get(error));
      }
    );
  }

  handleButtonPanelActions(action: string) {
    switch (action) {
      case 'Edit': {
        this.lookupSearchService.displayMode = DISPLAY_MODE.EDIT;
        this.enableButtons();
        break;
      }
      case 'Save': {
        const saveSuccessful = this.actionService.save();
        if (saveSuccessful) {
          this.lookupSearchService.displayMode = DISPLAY_MODE.VIEW_PAGE;
          this.enableButtons();
        }
        break;
      }
      case 'Delete Line Item(s)': {
        this.actionService.deleteSearchLineItem();
        break;
      }
      case 'Delete All Line Items': {
        this.actionService.deleteAllLineItems();
        break;
      }
    }
  }

  createSearchCriteria() {
    const defaultSearchCriteria = new SearchCriteria();
    defaultSearchCriteria.conditions = [];
    defaultSearchCriteria.loadDefaultCriteria = false;

    const defaultCondition = this.createSearchCondition(1);
    const defaultClause = new Clause();
    defaultClause.column = SEARCH_KEYS.RECEIPT_KEY;
    defaultClause.value = this.receiptKey;
    defaultClause.operation = '=';
    defaultCondition.clauses = [defaultClause];

    defaultSearchCriteria.conditions.push(defaultCondition);

    return defaultSearchCriteria;
  }

  createSearchCondition(id: any): Condition {
    const defaultCondition = new Condition();
    defaultCondition.id = id;
    defaultCondition.displayMode = 'VIEW';
    defaultCondition.isReadOnly = true;
    defaultCondition.isMandatory = true;
    return defaultCondition;
  }

  isColumnEditable(params: any) {
    const componentRef = params.context.componentParent;
    // if the table is in view mode, do not make any column as editable
    if (componentRef.tableMode === DISPLAY_MODE.VIEW_PAGE) {
      return false;
    }

    const rowIndex: number = params.node.rowIndex;
    // if the finalizeFlag is set to "Y", do not allow editing
    const finalizeCell = componentRef.pageRows[rowIndex].cells[META_DATA.FINALIZE_FLAG];
    if(finalizeCell && finalizeCell.cVal === YES) {
      return false;
    }
    // enable the column only if it is an editable field in the metadata
    return (componentRef.metaDataMap[params.colDef.colId].displayType === DISPLAY_MODE.EDIT);
  }

  getRowStyle(params: any) {
    const isFinalized = params.data[META_DATA.FINALIZE_FLAG].cVal === YES;
    const isEditMode = params.context.componentParent.tableMode === DISPLAY_MODE.EDIT;
    const rowOpacity = isFinalized && isEditMode ? 0.6 : 1;
    if (params.node.rowIndex % 2 === 0) {
      return { 'background-color': TABLE_STYLE.ROW_COLOR, 'color': TABLE_STYLE.TEXT_COLOR, 'opacity': rowOpacity };
    } else {
      return { 'background-color': TABLE_STYLE.ALT_ROW_COLOR, 'color': TABLE_STYLE.TEXT_COLOR, 'opacity': rowOpacity };
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/shared/lookup/serial-number-popup/serial-number-popup.component.ts